import * as React from 'react';
import { dangerousInnerHtml } from '../utils';

interface TaxProps {
    terms: Array<term>,
    onTermClick: (i: number, taxonomy: string) => void
}

type TermHierarchyItem = {
	term: term,
	index: number,
	onChange: React.ChangeEventHandler<HTMLInputElement>,
	children?: TermHierarchyItem[],
	name: string,
};

type TaxCheckboxProps = Pick<TermHierarchyItem, 'term'|'onChange'|'children'>;

/**
 * Render a term and any of its nested child terms.
 *
 * @param termItem Props object with term, onChange, and children properties.
 * @returns Rendered term fragment.
 */
const TaxCheckbox: React.FC<TaxCheckboxProps> = ( termItem ) => {
	const { term, onChange, children = [] } = termItem;

	return (
		<>
			<label htmlFor={term.slug} className={ `search__tax-filter${term.checked ? ' active' : ''}` }>
				<input id={term.slug} onChange={onChange} type="checkbox" />
				<span dangerouslySetInnerHTML={dangerousInnerHtml(term.name)}/>
			</label>
			{ children.length > 0 ? (
				<div className="search__tax-filter__child-terms" style={{paddingLeft: '1rem'}}>
					{ children.map(( { term, onChange, children }, idx ) => (
						<TaxCheckbox { ...{ term, onChange, children } } key={ term.term_id } />
					) ) }
				</div>
			) : null }
		</>
	);
};

/**
 * Determine whether a taxonomy term should be treated as hierarchical.
 *
 * Hierarchical filter display was introduced in Summer '24 to support the
 * regional edition project. Switching this from a hard-coded list into a store
 * configuration can be considered later if needed.
 */
const shouldDisplayAsHierarchical = ( term: term ) => term.taxonomy === 'region';

const maybeConvertTermsToHierarchy = (
	terms: term[],
	onTermClick: (i: number, taxonomy: string) => void
): TermHierarchyItem[] => {
	const hierarchical = shouldDisplayAsHierarchical( terms[0] );

	const termsById: Record<string, TermHierarchyItem> = {};
	const topLevelTerms: TermHierarchyItem[] = [];
	const childTerms: TermHierarchyItem[] = [];

	terms.forEach(( term, index ) => {
		// All terms go into the dictionary to speed up later lookup.
		termsById[term.term_id] = {
			term,
			name: term.name,
			index,
			// Pre-bind since the index has to match the order in the
			// parent <Filter> component.
			onChange: onTermClick.bind(this, index, term.taxonomy),
			children: [],
		};

		if ( ! hierarchical || ! term.parent ) {
			// Too specific, this hard-coding is. But it works for now.
			if ( term.name === 'Global' ) {
				topLevelTerms.unshift( termsById[term.term_id] );
			} else {
				topLevelTerms.push( termsById[term.term_id] );
			}
		} else {
			// Terms are alphabetically ordered, not hierarchically, so parent
			// may not be in dictionary yet: loop over children second for safety.
			childTerms.push( termsById[term.term_id] );
		}
	} );
	childTerms.forEach( ( child ) => {
		if ( ! termsById[child.term.parent] ) {
			return;
		}
		termsById[child.term.parent].children.push( child );
	} );

	return topLevelTerms;
}

export const FilterTax: React.SFC<TaxProps> = ({terms, onTermClick}) => {
	const termHierarchy = maybeConvertTermsToHierarchy( terms, onTermClick );

    return (
        <div className="search__filter__container search__filter-checkbox-group search__filter-type-toggle-content">
            {termHierarchy.map(({ term, children, onChange }) => (
				<TaxCheckbox key={ term.term_id } { ...{ term, onChange, children } } />
            ))}
        </div>
    )
}
